/**
 * RowManager.jsx
 * Sidebar panel for managing rows and seat counts in the seating map editor.
 * Rows are positioned by dragging directly on the canvas.
 */
import React from 'react';
import {
  Box, Typography, IconButton, Button, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';

const navy = '#1e3a5f';

const RowManager = ({
  rows,
  onAddRow,
  onDuplicateRow,
  onRemoveRow,
  onUpdateSeatCount,
  onOpenSeatNames,
  onHoverRow,
}) => {
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: navy }}>
          שורות ({rows.length})
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddRow}
          sx={{ color: navy, fontSize: 12 }}
        >
          הוסף שורה
        </Button>
      </Box>

      <Typography variant="caption" sx={{ color: '#aaa', fontSize: 10, display: 'block', mb: 1 }}>
        גרור שורה על המפה למיקום
      </Typography>

      {/* Row list */}
      {rows.map((row, index) => (
        <Box
          key={row.id}
          onMouseEnter={() => onHoverRow?.(row.id)}
          onMouseLeave={() => onHoverRow?.(null)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            py: 0.5,
            px: 0.5,
            borderBottom: index < rows.length - 1 ? '1px solid #f0f0f0' : 'none',
            borderRadius: 1,
            '&:hover': { bgcolor: '#fefce8' },
          }}
        >
          {/* Row label */}
          <Typography variant="caption" sx={{ minWidth: 42, color: '#888', fontSize: 12 }}>
            שורה {index + 1}
          </Typography>

          {/* Seat count stepper */}
          <IconButton
            size="small"
            onClick={() => onUpdateSeatCount(row.id, Math.max(1, row.seats.length - 1))}
            sx={{ p: 0.3 }}
          >
            <RemoveIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 18, textAlign: 'center', fontSize: 13, fontWeight: 600 }}>
            {row.seats.length}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onUpdateSeatCount(row.id, row.seats.length + 1)}
            sx={{ p: 0.3 }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>

          {/* Name seats */}
          <Tooltip title="שיוך שמות">
            <IconButton
              size="small"
              onClick={() => onOpenSeatNames(row)}
              sx={{ color: navy, ml: 0.3 }}
            >
              <PersonIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {/* Duplicate row */}
          <Tooltip title="שכפל שורה">
            <IconButton
              size="small"
              onClick={() => onDuplicateRow(row.id)}
              sx={{ color: '#666', p: 0.3 }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>

          {/* Remove row */}
          {rows.length > 1 && (
            <Tooltip title="מחק שורה">
              <IconButton
                size="small"
                onClick={() => onRemoveRow(row.id)}
                sx={{ color: '#999', p: 0.3 }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default RowManager;
